import * as React from 'react';
import { useLingui, Trans } from '@lingui/react/macro';
import { IShowroomErrorEvent, IShowroomProps, Showroom } from 'components/Showroom';
import Header from 'components/Header';
import Footer from 'components/Footer';
import { isValidSessionID } from 'utils/session';
import { startNewSession, streamCanStart, userSession } from 'state/session';
import { classNames } from 'utils/ui/css';
import { fireEvent, IntegrationType, integrationType } from 'utils/ui/app';
import { useWebComponent } from 'components/WebComponentRoot';
import { tenantConfig } from 'utils/tenant';
import { myCameraStream } from 'state/video';
import { initWebRtc, voipRtcConnection } from 'utils/ui/rtc';
import { user } from 'utils/auth/user';
import { analyticsState } from 'state/analytics';
import { BandwidthOptimisationConfig } from 'hooks/useBandwidthOptimisation';
import { ExperienceSwitcher } from 'containers/ExperienceSwitcher';
import Loader from 'components/Loader';
import useUnloadEvents from './useUnloadEvents';
import MultiVoip from './Voip/MultiVoip';
import Gallery from 'components/Micro2DApp/Gallery';
import Compare from 'components/Micro2DApp/Compare';
import { getComparisonVehicle } from 'state/session/compare';
import Icon from 'components/Icon';

export interface IStreamViewProps {
  className?: string;
  userType?: IShowroomProps['userType'];
  /**
   * Indicates whether the component is currently expanded or collapsed.
   * This is a controlled prop that reflects the current state of expansion.
   */
  expanded?: boolean;
  useVoip?: boolean;
  /**
   * Indicates if this instance of the StreamView is the session host.
   * This is used to determine if it is able to start new 3D sessions or load 2D content.
   * This handles cases when the active experience has not been fully loaded by the host yet.
   */
  isSessionHost?: boolean;

  onError?: () => void;
  bandwidthOptimisation?: BandwidthOptimisationConfig & {
    enabled?: boolean;
  };
}

const StreamView: React.FC<IStreamViewProps> = ({
  userType = user.type,
  onError,
  className,
  useVoip = false,
  expanded = false,
  isSessionHost = true,
  bandwidthOptimisation = { enabled: false },
}) => {
  const { t } = useLingui();
  const session = userSession().useState();
  const elementRef = React.useRef<HTMLDivElement>(null);
  const { hasSlot } = useWebComponent();
  tenantConfig().useState();
  const cameraStream = myCameraStream().useState();
  const [voipStream, setVoipStream] = React.useState<boolean>(false);
  const [experience3DFailed, setExperience3DFailed] = React.useState<boolean>(false);
  useUnloadEvents(userType);

  const isBroadcastSession = session.sharingType === 'broadcast' || session.sharingType === 'broadcast-local';
  const isWebComponent = integrationType() === IntegrationType.WebComponent;

  // Get the comparing vehicle (if any)
  const [, comparingVehicle] = getComparisonVehicle();

  React.useEffect(() => {
    if (session.carStatus === 'ready' && session.activeExperience === '2D' && !session.id && user.type === 'dealer') {
      startNewSession();
      userSession().setValues({ carImagesStatus: 'loading' });
    }
    setExperience3DFailed(false);
  }, [session.id, session.carStatus, session.activeExperience]);

  React.useEffect(() => {
    if (
      session.streamActive &&
      session.customerAllowed && // this flag is set by the dealer when waiting room is closed
      session.sharingType === 'remote' &&
      tenantConfig().isAllowed('voip') &&
      (!voipRtcConnection || voipRtcConnection?.sessionId !== session.id) // reinitialize if the session id has changed
    ) {
      initWebRtc(session.id, () => {
        setVoipStream(true);
      });
    } else if (voipRtcConnection) {
      voipRtcConnection.destroy();
      setVoipStream(false);
    }
    return () => {
      setVoipStream(false);
    };
  }, [session.customerAllowed, session.id, session.sharingType, session.streamActive]);

  React.useEffect(() => {
    if ((cameraStream.isAudioEnabled || cameraStream.isVideoEnabled) && !analyticsState().get('voipUsed')) {
      analyticsState().setValues({ voipUsed: true });
    }
  }, [cameraStream.isAudioEnabled, cameraStream.isVideoEnabled]);

  const onAveError = (error: IShowroomErrorEvent) => {
    onError?.();

    if (tenantConfig().isAllowed('2D')) {
      userSession().setValues({
        activeExperience: '2D',
      });
    } else {
      // If 3D fails and 2D is not allowed, we show a fallback screen
      setExperience3DFailed(true);
    }

    const canContinue = fireEvent({
      el: elementRef.current,
      name: 'e3d-stream-error',
      detail: { userType, session, error },
    });
    if (!canContinue) return;

    console.error('Error connecting to stream', error);
    return false;
  };

  const onStreamConnected = () => {
    const canContinue = fireEvent({ el: elementRef.current, name: 'e3d-stream-start', detail: { userType, session } });
    if (!canContinue) {
      userSession().setValues({ streamActive: false });
    }
  };

  if (!userSession().sessionReadyForExperience) return null;

  const experience2D: React.JSX.Element = (
    <>
      {session.carImagesStatus === 'unset' && !user.isDealer && (
        <div className="ex3d-brand-page-background flex size-full flex-col items-center justify-center">
          <Icon className="ex3d-icon--car size-8 animate-bounce" />
          <h1 className="text-xl font-bold">
            <Trans>Vehicle Image Coming Soon</Trans>
          </h1>
          <p>
            <Trans>
              The image for this vehicle is currently being processed and will be available shortly. Thank you for your
              patience.
            </Trans>
          </p>
        </div>
      )}
      {session.carImagesStatus === 'unset' && user.isDealer && (
        <div className="relative z-0 size-full grow overflow-hidden bg-home-page bg-[length:100%_100%]" />
      )}
      {session.carImagesStatus === 'loading' && <Loader className="size-full" />}
      {session.carImagesStatus === 'ready' &&
        (session.comparisonMode === 'on' &&
        !!comparingVehicle?.carImages?.items.length &&
        session.carImages?.items?.length ? (
          <Compare
            car1={session.carImages?.items ?? []}
            car2={comparingVehicle.carImages?.items ?? []}
            brand={session.brand}
            template={session.brand}
            theme={session.brand}
            features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
            customerAllowance={session.customerAllowance}
          />
        ) : (
          <Gallery
            assets={session.carImages?.items ?? []}
            brand={session.brand}
            template={session.brand}
            theme={session.brand}
            features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
            customerAllowance={session.customerAllowance}
          />
        ))}
    </>
  );

  const experiences = [
    {
      name: '3D' as const,
      element: experience3DFailed ? (
        <div className="relative z-0 size-full grow overflow-hidden bg-home-page bg-[length:100%_100%]" />
      ) : // Make sure only session hosts can start new 3D sessions
      isSessionHost || (session.streamActive && session.aveSessionId) ? (
        <Showroom
          prString={session.prString}
          market={session.market!}
          brand={session.brand!}
          active={streamCanStart()}
          data-testid="3D"
          className={classNames(
            'grow bg-black',
            userType === 'customer' ? !session.customerAllowance && 'pointer-events-none' : '',
            expanded ? 'opacity-100' : 'absolute left-0 top-0 w-full grow object-cover'
          )}
          onConnected={onStreamConnected}
          onError={onAveError}
          sessionId={isValidSessionID(session.aveSessionId) ? session.aveSessionId : undefined}
          customerAllowance={session.customerAllowance}
          // [AVS-3617] Showroom's fullscreen should be disabled for the web component, but enabled for the standalone.
          // In standalone mode, the fullscreen button is only disabled in NERD broadcast sessions.
          features={{ fullscreen: !isWebComponent && session.sharingType !== 'broadcast' }}
          hideConsumptionData={true}
          onUpdateConsumptionData={(wltpText) => {
            if (wltpText) userSession().setValues({ wltpText });
          }}
          onUpdatePrString={(prString) => {
            if (prString) userSession().setValues({ prString, carDisplayName: t`Car configurator car` });
          }}
          bandwidthOptimisation={bandwidthOptimisation}
        />
      ) : (
        experience2D
      ), // Show 2D experience for customers / non-hosts in case 3D is still loading,
    },
    {
      name: '2D' as const,
      element: experience2D,
    },
  ];

  return (
    <div
      className={classNames('w-full h-full flex flex-col', className, isBroadcastSession && 'e3d-sixteen-nine')}
      ref={elementRef}
    >
      {expanded && <Header withControls={user.isDealer} />}
      {!hasSlot && <ExperienceSwitcher experiences={experiences} activeExperience={session.activeExperience} />}
      {(expanded || useVoip) && voipStream && <MultiVoip className="ex3d-stream--voip-container" controls />}
      {expanded && <Footer userType={userType} />}
    </div>
  );
};

export default StreamView;
